import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { BER_FOMENUK, BerFomenu, BerMenupont, lathatoPont, menupontNyit } from '../ber-menu';
import { modeNev, modeFavicon } from '../../mode-nevek';
import { KeretService } from '../../keret.service';
import { Subscription } from 'rxjs';
import { BerService, BerAktiv, elozoHonap } from '../ber.service';
import { honapNev } from '../ber.opciok';
import { DataService } from '../../data.service';

// BER – Bérszámfejtő burok. Fókusz témában a NAPLO fejlécének párja: sötét felső sáv a modul
// ikonjával, a főmenük lenyíló csempékkel (ber-menu.ts), jobbra az aktív ügyfél + hónap és a
// profil-lenyíló. Klasszikus témában a régi eszköztár (a menüt a bal sáv adja).
@Component({
  selector: 'app-ber-layout',
  templateUrl: './ber-layout.component.html',
  styleUrls: ['./ber-layout.component.scss'],
})
export class BerLayoutComponent implements OnInit, OnDestroy {
  // A fejlécben az alrendszer ÁTNEVEZHETŐ megjelenítési neve (mode-nevek.ts), nem bedrótozott szöveg.
  alrendszerNev = modeNev('BER');
  aktiv: BerAktiv = { intezmeny: '', ugyfelId: null, ugyfelNev: null, kivas: false, ...elozoHonap() };
  private sub?: Subscription;

  constructor(public router: Router, private ber: BerService, private data: DataService,
              private keret: KeretService) {}

  /** A modul valódi ikonja (appicons/ → assets/icon/mode/), mint a böngészőfülön és az appon. */
  modIkon = modeFavicon('BER');
  /** Fókusz téma: bal sáv nélkül, fül-sorral és profil-lenyílóval (modul-tema.ts). */
  get fokusz(): boolean { return this.keret.a?.tema() === 'fokusz'; }

  /*  A BÉR lapjai a Fókusz téma asztali fül-sorához (mobilon és Klasszikusban a bal sáv adja
   *  ugyanezeket a menü-táblából).
   *  Csak az látszik, amire a jogkörszerkesztőben van joga (Jogosultságok → BER). */
  private static readonly OLDALAK = [
    { k: 'ugyfelek', c: 'Ügyfelek', ut: '/ber/ugyfelek', ikon: 'business-outline' },
    { k: 'alkalmazottak', c: 'Alkalmazottak', ut: '/ber/alkalmazottak', ikon: 'people-outline' },
    { k: 'szamfejtes', c: 'Számfejtés', ut: '/ber/szamfejtes', ikon: 'calculator-outline' },
    { k: 'listak', c: 'Listák & átutalás', ut: '/ber/listak', ikon: 'stats-chart-outline' },
    { k: 'atalany', c: 'Átalányadós', ut: '/ber/atalany', ikon: 'cash-outline' },
    { k: 'beallitasok', c: 'Beállítások', ut: '/ber/beallitasok', ikon: 'settings-outline' },
  ];
  oldalak: { k: string; c: string; ut: string; ikon: string }[] = [];

  honapNev = honapNev;

  /* ── Fókusz főmenü ── */
  menuk: { f: BerFomenu; pontok: BerMenupont[] }[] = [];
  nyitottMenu: string | null = null;
  aktivPont: string | null = null;
  private navSub?: Subscription;
  readonly modBill = /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent) ? '⌥' : 'Alt+';

  async ngOnInit() {
    await this.ber.init();
    this.ber.aktivUgyfelFrissit();
    this.sub = this.ber.aktiv$.subscribe((a) => (this.aktiv = a));
    let szabad: (u: string) => boolean = () => true;
    try {
      const jog = await this.data.hozzaferesek();
      szabad = jog.szabad;
      this.oldalak = BerLayoutComponent.OLDALAK.filter((o) => jog.szabad(o.ut));
    } catch { this.oldalak = BerLayoutComponent.OLDALAK; }
    this.menuk = BER_FOMENUK.map((f) => ({ f, pontok: f.pontok.filter((p) => szabad(p.jog)) })).filter((x) => x.pontok.length);
    this.aktivKeres();
    this.navSub = this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => { this.nyitottMenu = null; this.aktivKeres(); });
    document.body.classList.add('ber-aktiv');   // a BÉR-ablakok témája (global.scss)
  }
  ngOnDestroy() { this.ber.kilepes(); this.sub?.unsubscribe(); this.navSub?.unsubscribe(); document.body.classList.remove('ber-aktiv'); }

  /** A főmenü felirata a gyorsbillentyű betűjének aláhúzásához. */
  cimReszek(f: BerFomenu): [string, string, string] {
    const i = f.cim.toLowerCase().indexOf(f.bill);
    return i < 0 ? [f.cim, '', ''] : [f.cim.slice(0, i), f.cim[i], f.cim.slice(i + 1)];
  }
  menuValt(k: string, ev?: Event) { ev?.stopPropagation(); this.nyitottMenu = this.nyitottMenu === k ? null : k; }
  /** Az Átalányadós menüpont csak átalányadózó ügyfélnél látszik (Ügyfelek → adózási forma). */
  get lenyiloPontok(): BerMenupont[] { return (this.menuk.find((m) => m.f.kulcs === this.nyitottMenu)?.pontok || []).filter((p) => lathatoPont(p, this.aktiv)); }
  /** A fejléc főmenüi az ügyfél szerint: amelyiknek nem marad pontja, nem látszik. */
  get lathatoMenuk() { return this.menuk.filter((m) => m.pontok.some((p) => lathatoPont(p, this.aktiv))); }
  nyit(p: BerMenupont) { this.nyitottMenu = null; menupontNyit(p, this.router, this.keret); }
  get aktivFomenu(): string | null { return this.menuk.find((m) => m.pontok.some((p) => p.kulcs === this.aktivPont))?.f.kulcs || null; }
  private aktivKeres() {
    const t = this.router.parseUrl(this.router.url);
    const ut = '/' + (t.root.children['primary']?.segments.map((x) => x.path).join('/') || '');
    const q = t.queryParams || {};
    let talalt: string | null = null;
    for (const m of this.menuk) for (const p of m.pontok) {
      if (p.ut !== ut) continue;
      if (p.query && !Object.entries(p.query).every(([k, v]) => (q[k] || (k === 'nezet' ? 'berlista' : '')) === v)) continue;
      talalt = talalt || p.kulcs;
    }
    this.aktivPont = talalt;
  }

  /** Alt (Macen ⌥) + a főmenü aláhúzott betűje; a fizikai billentyűből (e.code), Esc bezár. */
  @HostListener('window:keydown', ['$event'])
  billentyu(e: KeyboardEvent) {
    if (e.key === 'Escape' && this.nyitottMenu) { this.nyitottMenu = null; return; }
    if (!this.fokusz || !e.altKey || e.ctrlKey || e.metaKey) return;
    const betu = (/^Key([A-Z])$/.exec(e.code || '')?.[1] || String(e.key || '')).toLowerCase();
    const m = this.lathatoMenuk.find((x) => x.f.bill === betu);
    if (m) { e.preventDefault(); this.nyitottMenu = m.f.kulcs; }
  }

  private static readonly CIMEK: Record<string, string> = {
    ugyfelek: 'Ügyfelek', alkalmazottak: 'Alkalmazottak', szamfejtes: 'Számfejtés',
    listak: 'Listák & átutalás', atalany: 'Átalányadós', beallitasok: 'Beállítások', fomenu: 'Főmenü',
  };

  aktivSzegmens(): string {
    const u = this.router.url;
    if (u.startsWith('/ber/alkalmazottak')) return 'alkalmazottak';
    if (u.startsWith('/ber/szamfejtes')) return 'szamfejtes';
    if (u.startsWith('/ber/listak')) return 'listak';
    if (u.startsWith('/ber/atalany')) return 'atalany';
    if (u.startsWith('/ber/beallitasok')) return 'beallitasok';
    if (u.startsWith('/ber/fomenu')) return 'fomenu';
    return 'ugyfelek';
  }

  /** A Klasszikus fejléc oldalcíme: a menüpont címe az útvonal szerint (így minden új oldalnak van),
   *  különben a régi szegmens-térkép. */
  oldalCim(): string {
    const t = this.router.parseUrl(this.router.url);
    const ut = '/' + (t.root.children['primary']?.segments.map((x) => x.path).join('/') || '');
    const q = t.queryParams || {};
    for (const f of BER_FOMENUK) for (const p of f.pontok) {
      if (p.ut === ut && (!p.query || Object.entries(p.query).every(([k, v]) => (q[k] || (k === 'nezet' ? 'berlista' : '')) === v))) return p.cim;
    }
    if (ut.startsWith('/ber/bevallas/')) return 'Nyomtatványkitöltő';
    return BerLayoutComponent.CIMEK[this.aktivSzegmens()] || this.alrendszerNev;
  }

  open(url: string) { this.router.navigateByUrl(url); }
}
